// src/modules/accounting/pages/FundContributionsPage.jsx
import React, { useEffect, useState } from 'react';
import {
  Button, Typography, Tag, Row, Col, Modal, Form, Input, InputNumber,
  Select, DatePicker, message, Space, Card, Statistic, Popconfirm, Divider,
  Alert, Spin, Descriptions, Tabs
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DollarOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  fetchContributions,
  getContributionById,
  createContribution,
  updateContribution,
  deleteContribution,
  settleContribution,
  getContributionSummary,
  fetchUsers,
  fetchFunds,
  getFundBalance,
} from '../api';
import { fetchPledgeReleaseCases } from '../../pledgeReleaseCase/api';
import { fetchWalkInCases } from '../../walkInCase/api';
import FundsPage from './FundsPage';
import PaymentSplitInput from '../../../components/PaymentSplitInput';
import DataTable from '../../../components/UI/DataTable';

const { Text } = Typography;

const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const PAYMENT_MODES = ['CASH', 'BANK_TRANSFER', 'UPI', 'CHEQUE', 'CREDIT_CARD', 'AUTO_DEBIT'];
const STATUS_COLORS = { ACTIVE: 'blue', PARTIALLY_SETTLED: 'orange', SETTLED: 'green' };

export default function FundContributionsPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();

  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [activeRecord, setActiveRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [createForm] = Form.useForm();
  const [settleForm] = Form.useForm();

  const [caseOptions, setCaseOptions] = useState([]);
  const [caseSearch, setCaseSearch] = useState('');
  const [caseSearching, setCaseSearching] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);

  // Funds
  const [funds, setFunds] = useState([]);
  const [fundsLoading, setFundsLoading] = useState(false);

  // Case type toggle (pledge vs walk-in vs others)
  const [caseType, setCaseType] = useState('PledgeReleaseCase');

  const [destinationSourceType, setDestinationSourceType] = useState('COMPANY');

  useEffect(() => { load(); loadSummary(); loadUsers(); }, []);
  useEffect(() => { loadFunds(); }, []);
  useEffect(() => { load(); }, [pagination.current, pagination.pageSize, statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchContributions({
        page: pagination.current,
        limit: pagination.pageSize,
        ...(statusFilter ? { status: statusFilter } : {}),
      });
      setData(res.data || []);
      setPagination((p) => ({ ...p, total: res.meta?.total || 0 }));
    } catch { messageApi.error('Failed to load contributions'); }
    finally { setLoading(false); }
  };

  const loadSummary = async () => {
    try {
        const res = await getContributionSummary(); 
        setSummary(res); // Set the full response, not just res.data
    }
    catch (err) { 
        console.error('❌ Failed to load summary:', err);
        /* non-critical */ 
    }
  };

  const loadUsers = async () => {
    try {
      const res = await fetchUsers();
      setUsers((res.data || res || []).map((u) => ({ value: u._id, label: `${u.name} (${u.designation || u.role || ''})` })));
    } catch { /* non-critical */ }
  };

  const loadFunds = async () => {
    setFundsLoading(true);
    try {
      const res = await fetchFunds();
      const rows = res.data || res || [];
      setFunds(rows.map((f) => ({
        value: f._id,
        label: `${f.name} — ${fmt(f.balance)}`,
        raw: f,
      })));
    } catch (e) {
      /* non-critical */
    } finally {
      setFundsLoading(false);
    }
  };

  const searchCases = async (value) => {
    setCaseSearch(value);
    if (!value || value.length < 2) {
      setCaseOptions([]);
      return;
    }
    setCaseSearching(true);
    try {
      if (caseType === 'WalkInPurchaseCase') {
        const res = await fetchWalkInCases({ search: value, limit: 20 });
        const rows = res.data || res || [];
        setCaseOptions(rows.map((c) => ({
          value: c._id,
          label: `${c.caseId} — ${c.customerId?.name || ''} — ${fmt(c.finalPaymentToCustomer)}`,
          raw: {
            _id: c._id,
            caseNumber: c.caseId,
            status: c.status,
            caseModel: 'WalkInPurchaseCase',
            finalPaymentToCustomer: c.finalPaymentToCustomer || 0,
          },
        })));
      } else {
        const res = await fetchPledgeReleaseCases({ search: value, limit: 20 });
        const rows = res.data || res || [];
        setCaseOptions(rows.map((c) => ({
          value: c._id,
          label: `${c.caseId} — ${c.customerId?.name || ''}`,
          raw: {
            _id: c._id,
            caseNumber: c.caseNumber,
            status: c.status,
            caseModel: 'PledgeReleaseCase',
          },
        })));
      }
    } catch {
      messageApi.error('Case search failed');
    } finally {
      setCaseSearching(false);
    }
  };

  const handleCaseSelect = async (caseId, option) => {
    const raw = option?.raw || caseOptions.find((o) => o.value === caseId)?.raw;
    setSelectedCase(raw || { _id: caseId });
    const model = raw?.caseModel || caseType;
    createForm.setFieldValue('caseId', caseId);
    createForm.setFieldValue('caseNumber', raw?.caseNumber || '');
    createForm.setFieldValue('caseModel', model);
  };

  const resetCreateModal = () => {
    createForm.resetFields();
    createForm.setFieldsValue({
      paymentSplits: [{ mode: 'BANK_TRANSFER', amount: null, reference: '' }],
    });
    setCaseSearch('');
    setCaseOptions([]);
    setSelectedCase(null);
    setEditingRecord(null);
    setCaseType('PledgeReleaseCase');
  };

  const handleCreate = async (values) => {
    setSubmitting(true);
    try {
      const isOthers = caseType === 'Others';
      const payload = {
        ...values,
        contributionDate: values.contributionDate?.toISOString(),
        destinationFundId: values.destinationFundId,
        // For Others, don't send caseId/caseModel
        ...(isOthers ? { caseId: undefined, caseModel: undefined, caseNumber: undefined } : {}),
      };

      if (editingRecord) {
        await updateContribution(editingRecord._id, payload);
        messageApi.success('Contribution updated');
      } else {
        await createContribution(payload);
        messageApi.success('Contribution recorded');
      }
      setCreateOpen(false);
      resetCreateModal();
      load(); loadSummary();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSettle = async (values) => {
    if (!activeRecord) return;
    setSubmitting(true);
    try {
      await settleContribution(activeRecord._id, values);
      messageApi.success('Settlement recorded');
      setSettleOpen(false);
      settleForm.resetFields();
      setActiveRecord(null);
      load(); loadSummary();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteContribution(id);
      messageApi.success('Deleted');
      load(); loadSummary();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to delete');
    }
  };

  const openEdit = async (record) => {
    setEditingRecord(record);
    setCreateOpen(true);
    createForm.resetFields();
    setCaseOptions([]);
    setSelectedCase(null);
    try {
      const res = await getContributionById(record._id);
      const detail = res.data || res || {};
      const caseIdValue = detail.caseId?._id || detail.caseId;
      const model = detail.caseModel || 'PledgeReleaseCase';
      setCaseType(detail.caseModel ? model : 'Others');

      const caseRaw = {
        _id: caseIdValue,
        caseNumber: detail.caseId?.caseNumber || detail.caseId?.caseId || detail.caseNumber,
        status: detail.caseId?.status || '',
        caseModel: model,
      };

      createForm.setFieldsValue({
        ...detail,
        caseId: caseIdValue,
        caseNumber: caseRaw.caseNumber,
        caseModel: detail.caseModel || undefined,
        contributor: detail.contributor?._id || detail.contributor,
        contributionDate: detail.contributionDate ? dayjs(detail.contributionDate) : undefined,
        destinationFundId: detail.destinationFundId?._id || detail.destinationFundId || undefined,
      });

      if (caseIdValue) {
        const option = {
          value: caseIdValue,
          label: `${caseRaw.caseNumber || caseIdValue} — ${caseRaw.status}`,
          raw: caseRaw,
        };
        setCaseOptions([option]);
        await handleCaseSelect(caseIdValue, option);
      }
    } catch (e) {
      messageApi.error('Failed to load contribution');
      setCreateOpen(false);
      setEditingRecord(null);
    }
  };

  const openDetail = async (record) => {
    setDetailOpen(true);
    setDetailData(null);
    try {
      const res = await getContributionById(record._id);
      setDetailData(res.data);
    } catch {
      messageApi.error('Failed to load details');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'contributionId', key: 'id', width: 110 },
    { title: 'Type', dataIndex: 'caseModel', key: 'type', width: 90,
      render: (v) => v === 'WalkInPurchaseCase'
        ? <Tag color="volcano">Walk-In</Tag>
        : v === 'PledgeReleaseCase'
          ? <Tag color="geekblue">Pledge</Tag>
          : <Tag color="default">Others</Tag> },
    { title: 'Contributor', key: 'contributor', width: 150,
      render: (_, r) => r.contributor?.name || '—' },
    { title: 'Case', dataIndex: 'caseNumber', key: 'case', width: 120,
      render: (v) => v || '—' },
    { title: 'Fund', key: 'fund', width: 130,
      render: (_, r) => r.destinationFundId?.name ? <Tag color="cyan">{r.destinationFundId.name}</Tag> : '—' },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 120,
      render: (v) => <Text strong>{fmt(v)}</Text> },
    { title: 'Settled', dataIndex: 'totalSettled', key: 'settled', width: 110,
      render: (v) => <Text style={{ color: '#3f8600' }}>{fmt(v)}</Text> },
    { title: 'Outstanding', dataIndex: 'outstandingAmount', key: 'out', width: 120,
      render: (v) => <Text style={{ color: v > 0 ? '#cf1322' : '#3f8600', fontWeight: 600 }}>{fmt(v)}</Text> },
    { title: 'Mode', dataIndex: 'paymentMode', key: 'mode', width: 120,
      render: (v, record) => {
        if (v === 'SPLIT' && record.paymentSplits?.length) {
          return record.paymentSplits.map((s, i) => (
            <Tag key={i} color="blue" style={{ marginBottom: 2 }}>
              {s.mode?.replace(/_/g, ' ')} — {fmt(s.amount)}
            </Tag>
          ));
        }
        return v?.replace(/_/g, ' ') || '—';
      } },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 130,
      render: (v) => <Tag color={STATUS_COLORS[v]}>{v?.replace(/_/g, ' ')}</Tag> },
    { title: 'Date', dataIndex: 'contributionDate', key: 'date', width: 100,
      render: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '—' },
    { title: 'Actions', key: 'actions', fixed: 'right', width: 220,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(r)}>View</Button>
          {r.status !== 'SETTLED' && (
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>
          )}
          {r.status !== 'SETTLED' && (
              <Button size="small" type="primary" icon={<DollarOutlined />}
              onClick={() => { 
                setActiveRecord(r); 
                settleForm.resetFields(); 
                setDestinationSourceType('COMPANY'); 
                setSettleOpen(true); 
              }}>
              Settle
            </Button>
          )}
          {r.status === 'ACTIVE' && r.totalSettled === 0 && (
            <Popconfirm title="Delete this contribution?" onConfirm={() => handleDelete(r._id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}

      <Tabs defaultActiveKey="contributions">
        <Tabs.TabPane key="contributions" tab="Contributions">

      {/* Summary cards */}
      {summary && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <Card size="small"><Statistic title="Total Contributed" value={fmt(summary.totals?.totalContributed)} valueStyle={{ fontSize: 18, fontWeight: 600 }} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small"><Statistic title="Total Settled" value={fmt(summary.totals?.totalSettled)} valueStyle={{ fontSize: 18, color: '#3f8600' }} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small"><Statistic title="Total Outstanding" value={fmt(summary.totals?.totalOutstanding)} valueStyle={{ fontSize: 18, color: '#cf1322', fontWeight: 600 }} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small"><Statistic title="Contributors" value={summary.data?.length || 0} suffix="people" valueStyle={{ fontSize: 18 }} /></Card>
            </Col>
          </Row>

          {/* Per-contributor breakdown */}
          {summary.data?.length > 0 && (
            <Card size="small" title="Per Contributor Summary" style={{ marginBottom: 16 }}>
              <DataTable persistId="accounting.fund-contributions.per-contributor"
                dataSource={summary.data}
                rowKey="contributorId"
                pagination={false}
                size="small"
                columns={[
                  { title: 'Name', dataIndex: 'contributorName', key: 'name', width: 150 },
                  { title: 'Designation', dataIndex: 'designation', key: 'desig', width: 120 },
                  { title: 'Contributed', dataIndex: 'totalContributed', key: 'c', width: 120, render: (v) => fmt(v) },
                  { title: 'Settled', dataIndex: 'totalSettled', key: 's', width: 120, render: (v) => <Text style={{ color: '#3f8600' }}>{fmt(v)}</Text> },
                  { title: 'Outstanding', dataIndex: 'totalOutstanding', key: 'o', width: 120,
                    render: (v) => <Text style={{ color: v > 0 ? '#cf1322' : '#3f8600', fontWeight: 600 }}>{fmt(v)}</Text> },
                  { title: '# Active', dataIndex: 'activeCount', key: 'ac', width: 80 },
                ]}
              />
            </Card>
          )}
        </>
      )}

      {/* Toolbar */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space>
            <Select placeholder="All Statuses" allowClear style={{ width: 180 }}
              value={statusFilter || undefined}
              onChange={(v) => { setPagination((p) => ({ ...p, current: 1 })); setStatusFilter(v || ''); }}>
              {['ACTIVE', 'PARTIALLY_SETTLED', 'SETTLED'].map((s) => (
                <Select.Option key={s} value={s}>{s.replace(/_/g, ' ')}</Select.Option>
              ))}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={() => { load(); loadSummary(); }}>Refresh</Button>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { resetCreateModal(); setCreateOpen(true); }}>
              Record Contribution
            </Button>
          </Space>
        </Col>
      </Row>


      <DataTable persistId="accounting.fund-contributions.main" serverPaginated
        dataSource={data} rowKey="_id" columns={columns}
        loading={loading} size="small" scroll={{ x: 1100 }}
        pagination={{
          current: pagination.current, pageSize: pagination.pageSize, total: pagination.total,
          showSizeChanger: true,
          onChange: (p, s) => setPagination({ current: p, pageSize: s, total: pagination.total }),
        }}
      />

        </Tabs.TabPane>
        <Tabs.TabPane key="funds" tab="Manage Company Funds">
          <div style={{ padding: 12 }}>
            <FundsPage />
          </div>
        </Tabs.TabPane>
      </Tabs>

      {/* ── Create Modal ── */}
      <Modal
        title={editingRecord ? 'Edit Fund Contribution' : 'Record Fund Contribution'}
        open={createOpen}
        onCancel={() => { setCreateOpen(false); resetCreateModal(); }}
        onOk={() => createForm.submit()}
        confirmLoading={submitting}
        okText={editingRecord ? 'Update' : 'Save'}
        width={640}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          {/* Hidden fields auto-filled on case select */}
          <Form.Item name="caseId" hidden><Input /></Form.Item>
          <Form.Item name="caseNumber" hidden><Input /></Form.Item>
          <Form.Item name="caseModel" hidden><Input /></Form.Item>

          {/* Step 0 — Case Type */}
          <Form.Item label="Case Type">
            <Select
              value={caseType}
              onChange={(v) => {
                setCaseType(v);
                setCaseOptions([]);
                setCaseSearch('');
                setSelectedCase(null);
                createForm.setFieldValue('caseId', undefined);
                createForm.setFieldValue('caseNumber', '');
                createForm.setFieldValue('caseModel', v === 'Others' ? undefined : v);
              }}
              options={[
                { value: 'PledgeReleaseCase', label: 'Pledge Release Case' },
                { value: 'WalkInPurchaseCase', label: 'Walk-In Purchase Case' },
                { value: 'Others', label: 'Others' },
              ]}
            />
          </Form.Item>

          {/* Step 1 — Search Case (hidden for Others) */}
          {caseType !== 'Others' && (
            <Form.Item label="Search Case" 
              help={caseType === 'WalkInPurchaseCase'
                ? 'Type 2+ characters to search walk-in cases by case ID'
                : 'Type 2+ characters to search pledge release cases by case number or customer name'}>
              <Select
                showSearch
                placeholder={<span><SearchOutlined /> Search by case number or customer…</span>}
                filterOption={false}
                onSearch={searchCases}
                onSelect={handleCaseSelect}
                loading={caseSearching}
                notFoundContent={caseSearching ? <Spin size="small" /> : caseSearch.length >= 2 ? 'No cases found' : 'Start typing to search'}
                options={caseOptions}
                style={{ width: '100%' }}
                allowClear
                onClear={() => {
                  setSelectedCase(null);
                  createForm.setFieldValue('caseId', undefined);
                  createForm.setFieldValue('caseNumber', '');
                  createForm.setFieldValue('caseModel', undefined);
                }}
              />
            </Form.Item>
          )}

          <Divider plain style={{ margin: '8px 0 12px' }}>Contribution Details</Divider>

          {/* Credit to Fund — required */}
          <Form.Item label="Credit to Fund" name="destinationFundId" rules={[{ required: true, message: 'Please select a fund to credit' }]}>
            {fundsLoading ? (
              <Spin size="small" />
            ) : (
              <Select 
                placeholder="Select fund to credit" 
                options={funds} 
                showSearch
                filterOption={(input, option) => {
                  if (!input || !option?.label) return true;
                  return option.label.toLowerCase().includes(input.toLowerCase());
                }}
                notFoundContent="No funds available"
                disabled={fundsLoading || funds.length === 0}
                onChange={(value) => {
                  createForm.setFieldValue('destinationFundId', value);
                  // Trigger validation to clear error
                  createForm.validateFields(['destinationFundId']);
                }}
              />
            )}
            <div style={{ marginTop: 6, color: '#666', fontSize: 12 }}>
              The contributed amount will be credited into this fund.
            </div>
          </Form.Item>

          {/* Contributor */}
          <Form.Item label="Contributor (Employee)" name="contributor" rules={[{ required: true }]}>
            <Select showSearch placeholder="Select employee" options={users}
              filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())} />
          </Form.Item>

          <Row gutter={12}>
            <Col span={24}>
              <Form.Item
                label={caseType === 'Others' ? 'Amount (₹)' : 'Contribution Amount (₹)'}
                name="amount"
                rules={[{ required: true }]}
                help={caseType === 'Others'
                  ? 'Amount to credit into the selected fund'
                  : 'Amount you are contributing'
                }
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider plain style={{ margin: '8px 0 12px' }}>Payment Breakdown</Divider>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.amount !== cur.amount}>
            {({ getFieldValue }) => (
              <PaymentSplitInput fieldName="paymentSplits" totalAmount={getFieldValue('amount') || 0} />
            )}
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Date" name="contributionDate" initialValue={dayjs()}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Transaction Ref" name="transactionReference">
                <Input placeholder="UTR / Cheque #" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Remarks" name="remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Settle Modal ── */}
      <Modal
        title={`Settle — ${activeRecord?.contributionId || ''}`}
        open={settleOpen}
        onCancel={() => { setSettleOpen(false); setActiveRecord(null); }}
        onOk={() => settleForm.submit()}
        confirmLoading={submitting}
        okText="Record Settlement"
      >
        {activeRecord && (
          <div style={{ marginBottom: 16, background: '#f6f6f6', padding: 12, borderRadius: 6 }}>
            <Row gutter={16}>
              <Col span={8}><Text type="secondary">Original</Text><br /><Text strong>{fmt(activeRecord.amount)}</Text></Col>
              <Col span={8}><Text type="secondary">Settled</Text><br /><Text style={{ color: '#3f8600' }}>{fmt(activeRecord.totalSettled)}</Text></Col>
              <Col span={8}><Text type="secondary">Outstanding</Text><br /><Text style={{ color: '#cf1322', fontWeight: 600 }}>{fmt(activeRecord.outstandingAmount)}</Text></Col>
            </Row>
          </div>
        )}
        <Form form={settleForm} layout="vertical" onFinish={handleSettle}>
          <Form.Item label="Settlement Amount (₹)" name="amount" rules={[{ required: true }]}>
            <InputNumber min={0} max={activeRecord?.outstandingAmount || 0} precision={2} style={{ width: '100%' }} />
          </Form.Item>

          {/* Source Fund - where to debit settlement amount from */}
          <Form.Item label="Deduct from Fund" name="sourceFundId" rules={[{ required: true, message: 'Please select a fund to deduct from' }]}>
            {fundsLoading ? <Spin size="small" /> : (
              <Select 
                placeholder="Select fund to deduct settlement amount from" 
                options={funds} 
                showSearch
                filterOption={(input, option) => {
                  if (!input || !option?.label) return true;
                  return option.label.toLowerCase().includes(input.toLowerCase());
                }}
                notFoundContent="No funds available"
                disabled={fundsLoading || funds.length === 0}
                onChange={(value) => {
                  settleForm.setFieldValue('sourceFundId', value);
                  // Trigger validation to clear error
                  settleForm.validateFields(['sourceFundId']);
                }}
              />
            )}
            <div style={{ marginTop: 6, color: '#666', fontSize: 12 }}>
              Settlement amount will be deducted from this fund.
            </div>
          </Form.Item>

          <Form.Item label="Destination Type" name="destinationSourceType" initialValue={destinationSourceType}>
            <Select value={destinationSourceType} onChange={(v) => { setDestinationSourceType(v); settleForm.setFieldValue('destinationSourceType', v); }}>
              <Select.Option value="COMPANY">Company Fund</Select.Option>
              <Select.Option value="INDIVIDUAL">Individual (Return to Person)</Select.Option>
            </Select>
          </Form.Item>

          {destinationSourceType === 'COMPANY' && (
            <Form.Item label="Credit to Fund (optional)" name="destinationFundId">
              {fundsLoading ? <Spin size="small" /> : (
                <Select placeholder="Select fund to credit (if different from source fund)" options={funds} allowClear />
              )}
              <div style={{ marginTop: 6, color: '#666', fontSize: 12 }}>Optional: If specified, the settlement amount will also be credited to this fund. Leave empty if same as source fund.</div>
            </Form.Item>
          )}
          <Form.Item label="Source" name="source" initialValue="SMELTER_PAYMENT">
            <Select options={[
              { value: 'SMELTER_PAYMENT', label: 'Smelter Payment (Gold Sale Recovery)' },
              { value: 'MANUAL', label: 'Manual / Cash Return' },
            ]} />
          </Form.Item>
          <Form.Item label="Remarks" name="remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Detail Modal ── */}
      <Modal
        title={`Contribution: ${detailData?.contributionId || ''}`}
        open={detailOpen}
        onCancel={() => { setDetailOpen(false); setDetailData(null); }}
        footer={null}
        width={650}
      >
        {!detailData ? <Spin style={{ display: 'block', margin: 40 }} /> : (
          (() => {
            const caseIdValue = detailData.caseId?._id || detailData.caseId;
            const caseLabel = detailData.caseNumber || detailData.caseId?.caseNumber || caseIdValue || '—';
            const batchLabel = detailData.inventoryBatchId?.batchId || detailData.inventoryBatchId?._id || detailData.inventoryBatchId || '—';
            const dispatchLabel = detailData.smelterDispatchId?.dispatchId || detailData.smelterDispatchId?._id || detailData.smelterDispatchId || '—';
            return (
              <>
                <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="Contributor">{detailData.contributor?.name}</Descriptions.Item>
                  <Descriptions.Item label="Designation">{detailData.contributor?.designation || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Amount">{fmt(detailData.amount)}</Descriptions.Item>
                  <Descriptions.Item label="Payment Mode">{detailData.paymentMode}</Descriptions.Item>
                  <Descriptions.Item label="Date">{new Date(detailData.contributionDate).toLocaleDateString('en-IN')}</Descriptions.Item>
                  <Descriptions.Item label="Status"><Tag color={STATUS_COLORS[detailData.status]}>{detailData.status}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Outstanding"><Text style={{ color: detailData.outstandingAmount > 0 ? '#cf1322' : '#3f8600', fontWeight: 600 }}>{fmt(detailData.outstandingAmount)}</Text></Descriptions.Item>
                  <Descriptions.Item label="Txn Ref">{detailData.transactionReference || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Case">{caseLabel}</Descriptions.Item>
                  <Descriptions.Item label="Case Type">
                    <Tag color={detailData.caseModel === 'WalkInPurchaseCase' ? 'volcano' : 'geekblue'}>
                      {detailData.caseModel === 'WalkInPurchaseCase' ? 'Walk-In' : 'Pledge'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Batch">{batchLabel}</Descriptions.Item>
                  <Descriptions.Item label="Dispatch">{dispatchLabel}</Descriptions.Item>
                  <Descriptions.Item label="Created By">{detailData.createdBy?.name || '—'}</Descriptions.Item>
                </Descriptions>
                {detailData.advancePaymentIds?.length > 0 && (
                  <Card size="small" title="Linked Advance Payments" style={{ marginBottom: 16 }}>
                    <DataTable persistId="accounting.fund-contributions.advances" showToolbar={false}
                      dataSource={detailData.advancePaymentIds}
                      rowKey={(r) => r._id || r}
                      pagination={false}
                      size="small"
                      columns={[
                        { title: 'Payment ID', dataIndex: 'paymentId', key: 'pid', render: (v, r) => v || r._id || '—' },
                        { title: 'Amount', dataIndex: 'amount', key: 'amt', render: (v) => fmt(v) },
                        { title: 'Date', dataIndex: 'paymentDate', key: 'date', render: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '—' },
                        { title: 'Status', dataIndex: 'status', key: 'status' },
                      ]}
                    />
                  </Card>
                )}
                {detailData.remarks && (
                  <Card size="small" style={{ marginBottom: 16 }}>
                    <Text type="secondary">Remarks:</Text> {detailData.remarks}
                  </Card>
                )}
                {detailData.settlements?.length > 0 && (
                  <>
                    <Divider orientation="left" plain>Settlement History</Divider>
                    <DataTable persistId="accounting.fund-contributions.settlements" showToolbar={false}
                      dataSource={detailData.settlements}
                      rowKey="_id"
                      size="small"
                      pagination={false}
                      columns={[
                        { title: 'Amount', dataIndex: 'amount', render: (v) => <Text strong style={{ color: '#3f8600' }}>{fmt(v)}</Text> },
                        { title: 'Source Fund', key: 'sourceFund', render: (_, r) => r.sourceFundId?.name ? <Tag color="red">{r.sourceFundId.name}</Tag> : '—' },
                        { title: 'Dest Fund', key: 'destFund', render: (_, r) => r.destinationFundId?.name ? <Tag color="green">{r.destinationFundId.name}</Tag> : '—' },
                        { title: 'Source', dataIndex: 'source', render: (v) => <Tag>{v?.replace(/_/g, ' ')}</Tag> },
                        { title: 'Date', dataIndex: 'settledDate', render: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '—' },
                        { title: 'By', key: 'by', render: (_, r) => r.settledBy?.name || '—' },
                        { title: 'Remarks', dataIndex: 'remarks', render: (v) => v || '—' },
                      ]}
                    />
                  </>
                )}
              </>
            );
          })()
        )}
      </Modal>
    </div>
  );
}
